import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  LobbyCharacterInfo,
  LobbyParty,
  PartyJointDecision,
  PendingInvite,
} from '@danda/shared';
import { websocketService } from '@/lib/websocket-service';
import { RoomTypeEnum } from '@danda/shared';
import type { Character, ChatRoom } from '@/lib/stores/lobbyStore.types';
import type { SlashCommandDefinition } from '../chat-commands';
import { ChatComposer } from './ChatComposer';
import { TypingIndicator } from './TypingIndicator';
import { PendingInvitesBanner } from './PendingInvitesBanner';
import { PartyParticipants } from './PartyParticipants';
import { PartyVoteBanner } from './PartyVoteBanner';
import { useLobbyMessageQueue } from '@/hooks/useLobbyMessageQueue';
import { AT_BOTTOM_THRESHOLD_PX, useStickToBottom } from '@/hooks/useStickToBottom';
import { useChatScrollKickStore } from '@/lib/stores/chatScrollKickStore';
import { useLobbyStore } from '@/lib/stores/lobbyStore';

interface LobbyRoomContentProps {
  room: ChatRoom;
  lobbyVirtuosoRef: React.RefObject<VirtuosoHandle | null>;
  renderMessageItem: (
    msg: import('@/lib/stores/lobbyStore').ChatMessage,
    index: number,
    roomType: string,
    revealProps?: { isQueuedReveal: boolean; onRevealComplete: () => void }
  ) => React.ReactNode;
  isWaitingForResponse: boolean;
  pendingInvites: PendingInvite[];
  isLobbyOnline: boolean;
  roomInputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  lobbyCharacters: LobbyCharacterInfo[];
  characters: Character[];
  parties: LobbyParty[];
  currentPartyId: string | null;
  selectedCharacterId: string | null;
  partyDecisions: Record<string, PartyJointDecision | undefined>;
  onDecisionResolved?: (partyId: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isActiveRoom: boolean;
  isTavianRoom: boolean;
  isTavianPresent?: boolean;
  /** Filtered slash commands to show in menu */
  slashCommands?: SlashCommandDefinition[];
  /** Currently selected command index */
  slashCommandIndex?: number;
  /** Callback when a command is selected */
  onSlashCommandSelect?: (command: SlashCommandDefinition) => void;
  /** Callback to close the slash command menu */
  onSlashCommandClose?: () => void;
}

export function LobbyRoomContent({
  room,
  lobbyVirtuosoRef,
  renderMessageItem,
  isWaitingForResponse,
  pendingInvites,
  isLobbyOnline,
  roomInputValue,
  onInputChange,
  onKeyPress,
  onSend,
  lobbyCharacters,
  characters,
  parties,
  currentPartyId,
  selectedCharacterId,
  partyDecisions,
  onDecisionResolved,
  inputRef,
  isActiveRoom,
  isTavianRoom,
  isTavianPresent,
  slashCommands,
  slashCommandIndex,
  onSlashCommandSelect,
  onSlashCommandClose,
}: LobbyRoomContentProps) {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);

  // Per-party "Tavian is composing a post-session narration" flags. Drives the
  // recovery typing dots below (see recoveryBrewing).
  const partyTyping = useLobbyStore((s) => s.partyTyping);

  // Progressive reveal queue for non-streamed NPC messages
  const {
    revealingMessageId,
    revealingMessageIsLiveStream,
    isQueueActive,
    finishReveal,
    skipQueue,
  } = useLobbyMessageQueue(room.id);

  const { userScrolledUp, userScrolledUpRef, setScroller, resetUserScrolledUp } =
    useStickToBottom();

  // All effects below early-out for inactive rooms: ChatPanel
  // force-mounts every room so each `LobbyRoomContent` runs its effects,
  // but `lobbyVirtuosoRef` is shared and points at the ACTIVE room's
  // Virtuoso. Without the `isActiveRoom` gate, a background stream or
  // teammate message would yank the visible room.

  // Auto-scroll during progressive reveal (bubble grows as text animates in)
  // — skip when the user has explicitly scrolled up to read history.
  useEffect(() => {
    if (!isActiveRoom || !revealingMessageId) return;
    const interval = setInterval(() => {
      if (userScrolledUpRef.current) return;
      lobbyVirtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
    }, 100);
    return () => clearInterval(interval);
  }, [isActiveRoom, revealingMessageId, lobbyVirtuosoRef, userScrolledUpRef]);

  // After reveal completes, do one final scroll to re-anchor at bottom
  const prevRevealingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isActiveRoom) {
      // Keep the ref in sync even when inactive — otherwise a reveal that
      // completed in a background room would fire a spurious scroll when
      // the user switches into the room (prev ref still holds the old id
      // but revealingMessageId is now null).
      prevRevealingRef.current = revealingMessageId;
      return;
    }
    if (prevRevealingRef.current && !revealingMessageId) {
      const timer = setTimeout(() => {
        if (userScrolledUpRef.current) return;
        lobbyVirtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
      }, 50);
      return () => clearTimeout(timer);
    }
    prevRevealingRef.current = revealingMessageId;
  }, [isActiveRoom, revealingMessageId, lobbyVirtuosoRef, userScrolledUpRef]);

  // Safety-net scroll AFTER streaming ends — when `isStreaming` flips to
  // false, `ChatMessage` swaps StreamingMarkdown for ReactMarkdown and may
  // suddenly render images/videos that were suppressed during streaming.
  // That height growth can leave the view above the new bottom. Two passes
  // (150ms + 500ms) catch the synchronous swap then async media loads.
  // Gated on `isActiveRoom` + `userScrolledUpRef` so it doesn't yank a
  // reader of history or scroll the visible room from a background room.
  const lastMessageStreaming = room.messages[room.messages.length - 1]?.isStreaming;
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (!isActiveRoom) {
      wasStreamingRef.current = !!lastMessageStreaming;
      return;
    }
    if (wasStreamingRef.current && !lastMessageStreaming) {
      const t1 = setTimeout(() => {
        if (userScrolledUpRef.current) return;
        lobbyVirtuosoRef.current?.scrollToIndex({
          index: 'LAST',
          align: 'end',
          behavior: 'auto',
        });
      }, 150);
      const t2 = setTimeout(() => {
        if (userScrolledUpRef.current) return;
        lobbyVirtuosoRef.current?.scrollToIndex({
          index: 'LAST',
          align: 'end',
          behavior: 'auto',
        });
      }, 500);
      wasStreamingRef.current = false;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    wasStreamingRef.current = !!lastMessageStreaming;
  }, [isActiveRoom, lastMessageStreaming, lobbyVirtuosoRef, userScrolledUpRef]);

  // Live-streaming bubble polls scroll-to-bottom while auto-scroll is on —
  // followOutput only fires on new items, not in-place text growth.
  const hasLiveStreaming = room.messages.some((m) => m.isStreaming);
  useEffect(() => {
    if (!isActiveRoom || !hasLiveStreaming) return;
    const interval = setInterval(() => {
      if (userScrolledUpRef.current) return;
      lobbyVirtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
    }, 100);
    return () => clearInterval(interval);
  }, [isActiveRoom, hasLiveStreaming, lobbyVirtuosoRef, userScrolledUpRef]);

  // Re-arm auto-follow + scroll on player-intent submission. Filtering by
  // senderId can't reliably identify local echoes (inner-voice / dead-char /
  // no-character rooms set senderId to auth uid in ChatPanel; server re-
  // broadcasts share the same senderId) — instead listen for the explicit
  // kick at submission time.
  const scrollKick = useChatScrollKickStore((s) => s.kick);
  const firstKickSeen = useRef(scrollKick);
  useEffect(() => {
    if (firstKickSeen.current === scrollKick) return; // mount tick, ignore
    firstKickSeen.current = scrollKick;
    if (!isActiveRoom) return;
    resetUserScrolledUp();
    lobbyVirtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
  }, [scrollKick, isActiveRoom, resetUserScrolledUp, lobbyVirtuosoRef]);

  // ArrowDown: skip current canned-typewriter bubble. Escape: dump all
  // remaining queued messages instantly. Both gated on `!isLiveStream` —
  // canned reveals AND live WS streams share `isStreaming: true`, so the
  // live-stream flag is the only reliable way to distinguish "safe to
  // truncate" (canned, content is stable) from "would corrupt mid-flight
  // chunks" (live WS).
  useEffect(() => {
    if (!isQueueActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA';
      if (
        e.key === 'ArrowDown' &&
        revealingMessageId &&
        !revealingMessageIsLiveStream &&
        !isInput
      ) {
        e.preventDefault();
        finishReveal(revealingMessageId);
      } else if (e.key === 'Escape') {
        // `skipRevealQueue` already preserves a live stream's flags at the
        // store level (see `lobbyStore.reveal-queue.ts::skipRevealQueue`),
        // so Escape can safely fire while a Kira/Aldric/Grolt stream is in
        // flight — it'll dump the canned queue tail and leave the live
        // stream alone.
        e.preventDefault();
        skipQueue();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isQueueActive, revealingMessageId, revealingMessageIsLiveStream, finishReveal, skipQueue]);

  const roomPartyId = room.id.startsWith('party-') ? room.id.replace('party-', '') : null;
  const isCurrentPartyRoom = room.type === RoomTypeEnum.PARTY && currentPartyId === roomPartyId;
  const activeDecision =
    roomPartyId && partyDecisions[roomPartyId] ? partyDecisions[roomPartyId] : undefined;
  const currentParty = currentPartyId ? parties.find((p) => p.id === currentPartyId) : undefined;

  // Post-session "summary brewing" dots. When a party returns to the lobby,
  // Tavian generates a sequence of SYSTEM narrations (session recap, long
  // rest, cooldown) with multi-second LLM gaps and no per-narration progress
  // signal, so the party chat looked frozen between bursts. The backend brackets
  // the whole recovery window with `lobby:party-typing` (true on start, false on
  // end), stored in `partyTyping[partyId]`. Show the dots whenever that flag is
  // set and nothing is actively streaming: dots fill each generation gap and
  // vanish the moment a narration starts streaming. (The party's
  // `lastSessionEnd.recoveryCompleted` is NOT usable here — it's flipped true at
  // recovery entry as a dedup guard, so the FE never observes it false.)
  const recoveryBrewing =
    room.type === RoomTypeEnum.PARTY &&
    roomPartyId != null &&
    partyTyping[roomPartyId] === true &&
    !hasLiveStreaming;

  const selectedChar = selectedCharacterId
    ? characters.find((c) => c.id === selectedCharacterId)
    : null;
  const isCharacterDead = selectedChar?.permanent?.status === 'dead';
  // Dead characters can only chat with Tavian (for resurrection). Block all other rooms.
  const isDeadAndBlocked = isCharacterDead && room.type !== RoomTypeEnum.TAVIAN;

  // Track scroll position via Virtuoso's built-in callbacks
  // These are more reliable than computing from rangeChanged during streaming layout shifts
  const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
    setIsScrolledToBottom(atBottom);
  }, []);

  const handleAtTopStateChange = useCallback((atTop: boolean) => {
    setIsScrolledToTop(atTop);
  }, []);

  // Scroll to bottom handler — also resets the "user scrolled up" flag
  // so subsequent messages resume auto-follow.
  const handleScrollToBottom = useCallback(() => {
    if (lobbyVirtuosoRef.current) {
      lobbyVirtuosoRef.current.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'smooth',
      });
      resetUserScrolledUp();
    }
  }, [lobbyVirtuosoRef, resetUserScrolledUp]);

  // Scroll to top handler
  const handleScrollToTop = useCallback(() => {
    if (lobbyVirtuosoRef.current) {
      lobbyVirtuosoRef.current.scrollToIndex({
        index: 0,
        align: 'start',
        behavior: 'smooth',
      });
    }
  }, [lobbyVirtuosoRef]);

  return (
    <div className="flex-1 flex flex-col mx-4 mt-0 h-full">
      {room.type === RoomTypeEnum.PARTY && (
        <PartyParticipants
          members={currentParty?.members}
          participants={room.participants}
          lobbyCharacters={lobbyCharacters}
          characters={characters}
          selectedCharacterId={selectedCharacterId}
          isTavianPresent={isTavianPresent ?? room.isTavianPresent}
        />
      )}

      {room.type !== RoomTypeEnum.PARTY && room.participants.length > 2 && (
        <div className="flex items-center gap-2 mb-2 pb-2 border-b">
          <Users className="h-4 w-4 text-muted-foreground" />
          <div className="flex items-center gap-1">
            {room.participants.slice(0, 3).map((participant) => (
              <div
                key={participant}
                className="h-6 w-6 rounded-full bg-secondary text-xs flex items-center justify-center"
              >
                {participant[0]?.toUpperCase() || '?'}
              </div>
            ))}
            {room.participants.length > 3 && (
              <span className="text-xs text-muted-foreground">+{room.participants.length - 3}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground truncate">
            {room.participants.join(', ')}
          </span>
        </div>
      )}

      {isCurrentPartyRoom && (
        <PartyVoteBanner
          decision={activeDecision}
          selectedCharacterId={selectedCharacterId}
          characters={characters}
          lobbyCharacters={lobbyCharacters}
          partyMembers={currentParty?.members}
          onResolved={onDecisionResolved}
        />
      )}

      <div className="flex-1 min-h-0 flex flex-col relative">
        {/* Scroll-to-top button — absolute so it never causes layout shifts */}
        {isActiveRoom && !isScrolledToTop && room.messages.length > 0 && (
          <div className="absolute top-2 left-0 right-0 flex justify-center z-10">
            <Button
              onClick={handleScrollToTop}
              size="icon"
              className="h-8 w-8 rounded-full shadow-lg bg-card border border-border hover:bg-accent text-card-foreground"
              aria-label="Scroll to top"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {isActiveRoom && (
            <Virtuoso
              ref={lobbyVirtuosoRef}
              scrollerRef={(el) => {
                setScroller(el as HTMLElement | null);
              }}
              style={{ height: '100%' }}
              data={room.messages}
              initialTopMostItemIndex={Math.max(0, room.messages.length - 1)}
              itemContent={(index, msg) => {
                // Each ProgressiveReveal gets a closure that captures its
                // OWN message id. The previous shared-ref pattern in
                // `useLobbyMessageQueue` misdirected the completion when
                // two reveals overlapped (canned Tavian queue mid-reveal +
                // live NPC stream-start). See `useLobbyMessageQueue.ts`.
                const isRevealing = msg.id === revealingMessageId;
                return renderMessageItem(
                  msg,
                  index,
                  room.type,
                  isRevealing && msg.isQueuedReveal
                    ? {
                        isQueuedReveal: true,
                        onRevealComplete: () => finishReveal(msg.id),
                      }
                    : undefined
                );
              }}
              followOutput={userScrolledUp ? false : 'auto'}
              alignToBottom
              atBottomStateChange={handleAtBottomStateChange}
              atTopStateChange={handleAtTopStateChange}
              atBottomThreshold={AT_BOTTOM_THRESHOLD_PX}
              components={{
                Footer: () => <div className="h-2" />,
              }}
            />
          )}
        </div>
        <TypingIndicator
          visible={(isWaitingForResponse || recoveryBrewing) && isActiveRoom}
          showLabel={false}
        />
        {/* Scroll-to-bottom button — absolute so it never causes layout shifts */}
        {isActiveRoom && !isScrolledToBottom && room.messages.length > 0 && (
          <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10 pointer-events-none">
            <Button
              onClick={handleScrollToBottom}
              size="icon"
              className="h-8 w-8 rounded-full shadow-lg bg-card border border-border hover:bg-accent text-card-foreground pointer-events-auto"
              aria-label="Scroll to bottom"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <PendingInvitesBanner
        visible={isTavianRoom}
        invites={pendingInvites}
        onAccept={(inviteId) => {
          // Keep the invite in the store until the server confirms: accept can
          // legitimately fail (party mid-adventure) and the invite stays live.
          // Success arrives as lobby:open-party-chat, which clears the list.
          websocketService.acceptPartyInvite(inviteId);
        }}
        onDecline={(inviteId) => {
          websocketService.declinePartyInvite(inviteId);
          // Decline is terminal client-side either way (a failed decline means
          // the invite was already gone), so drop it from the banner now.
          useLobbyStore.getState().removePendingInvite(inviteId);
        }}
      />

      <ChatComposer
        value={roomInputValue}
        inputRef={inputRef}
        onChange={onInputChange}
        onKeyDown={onKeyPress}
        onSend={onSend}
        placeholder={
          isDeadAndBlocked
            ? 'Your character is dead. Talk to Tavian to seek resurrection.'
            : isCharacterDead && room.type === RoomTypeEnum.TAVIAN
              ? 'Ask Tavian about resurrection...'
              : isLobbyOnline
                ? room.type === RoomTypeEnum.PARTY
                  ? 'Talk to your party. Press / for commands'
                  : `Talk to ${room.name}. Press / for commands`
                : 'Reconnecting...'
        }
        isOnline={isLobbyOnline && !isDeadAndBlocked}
        // Per-room id is required because ChatPanel mounts every chat room
        // with Radix `forceMount` (inactive rooms hidden via CSS, not unmounted),
        // so a constant id collides across rooms — `document.getElementById`
        // resolves to the first mounted composer (usually `main`) regardless of
        // which tab is active, breaking scenario-harness driven flows. Game
        // chat stays `#game-chat-input` because it renders a single
        // GameChatContent outside `chatRooms.map`. Scenario contract: use
        // `document.querySelector('[data-state="active"] [id^="lobby-chat-input"]:not([id$="-send"]):not([id$="-slash"])')`
        // to target the active room's input — both sibling buttons derive their
        // ids from this one, so both need excluding or the query returns a button.
        inputId={`lobby-chat-input-${room.id}`}
        slashCommands={slashCommands}
        slashCommandIndex={slashCommandIndex}
        onSlashCommandSelect={onSlashCommandSelect}
        onSlashCommandClose={onSlashCommandClose}
      />
    </div>
  );
}
